import { motion } from "framer-motion";
import { Database, ExternalLink, CloudUpload } from "lucide-react";
import { Link } from "react-router-dom";
import codegen from "../../../../docs/ontology/codegen.json";

// Read dataset list from the ontology, not hardcoded in the component
// (CLAUDE.md §1). codegen.json#kaggleDatasets is the single source of
// truth — adding a row there shows up here automatically.
interface KaggleDataset {
  n: number;
  id: string;
  name: string;
  author: string;          // derived from URL slug
  url: string;
  rows: string;
  license: string;
  updated: string;
  feeds: string;           // matches the "pillar" + extra context
  pillar: string;
  /** true when this dataset's columns map directly to SKU fields
   *  (label / category / price / leadDays / etc.). UI surfaces an
   *  "Import" CTA only on catalogable datasets. */
  importable: boolean;
}

interface CodegenKaggleEntry {
  id: string;
  n: number;
  label: string;
  url: string;
  rows: string;
  updated: string;
  license: string;
  pillar: string;
}

const PILLAR_TO_FEEDS: Readonly<Record<string, string>> = {
  Style:           "Style classifier · Intent grounding",
  "Room×Style":    "Room × Style mapping in sampleGraph",
  Material:        "Material ↔ Style edges",
  Catalog:         "SKU catalog · price · dimensions",
  "Vendor SLA":    "Vendor.onTimeRate · leadDays · SLA rules",
  Approvals:       "Approval(kind=RERA) · extensions · cost overruns",
  "Schedule/Cost/Risk": "Milestone schedule · BudgetLine shares",
  "Risk prediction":    "Cost-overrun Alert · risk score",
};

const IMPORTABLE_PILLARS: ReadonlySet<string> = new Set([
  "Catalog", "Vendor SLA", "Approvals", "Schedule/Cost/Risk",
]);

function authorFromUrl(url: string): string {
  const m = url.match(/datasets\/([^/]+)\//);
  return m && m[1] ? m[1] : "kaggle";
}

const DATASETS: KaggleDataset[] = ((codegen.kaggleDatasets ?? []) as ReadonlyArray<CodegenKaggleEntry>).map((d) => ({
  n: d.n,
  id: d.id,
  name: d.label,
  author: authorFromUrl(d.url),
  url: d.url,
  rows: d.rows,
  license: d.license,
  updated: d.updated,
  feeds: PILLAR_TO_FEEDS[d.pillar] ?? d.pillar,
  pillar: d.pillar,
  importable: IMPORTABLE_PILLARS.has(d.pillar),
}));

export function DataSourcesView(): JSX.Element {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-xl bg-ink-950 text-cream grid place-items-center">
          <Database className="w-4 h-4" />
        </span>
        <div>
          <div className="text-base font-semibold text-ink-950 leading-tight">Datasets imported</div>
          <div className="text-[12px] text-ink-500 leading-tight">
            10 verified Kaggle datasets back the demo. Every dataset feeds a specific node or edge type.
            Click any name to open the verified Kaggle URL.
          </div>
        </div>
      </div>

      <div className="panel overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-cream-deeper text-[10px] uppercase tracking-wider text-ink-500">
            <tr>
              <th className="text-left font-medium px-3 py-2">#</th>
              <th className="text-left font-medium px-3 py-2">Dataset</th>
              <th className="text-left font-medium px-3 py-2 hidden md:table-cell">Rows / Size</th>
              <th className="text-left font-medium px-3 py-2 hidden lg:table-cell">Updated</th>
              <th className="text-left font-medium px-3 py-2 hidden md:table-cell">License</th>
              <th className="text-left font-medium px-3 py-2">Feeds (graph node / edge)</th>
              <th className="text-right font-medium px-3 py-2"> </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {DATASETS.map((d, i) => (
              <motion.tr
                key={d.n}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: i * 0.02 }}
                className="hover:bg-cream-dark/60 transition-colors"
              >
                <td className="px-3 py-2 text-ink-500 tabular">{d.n}</td>
                <td className="px-3 py-2">
                  <div className="text-ink-950 font-medium leading-tight">{d.name}</div>
                  <div className="text-[10px] text-ink-500 font-mono leading-tight">{d.author}</div>
                </td>
                <td className="px-3 py-2 text-ink-700 hidden md:table-cell tabular">{d.rows}</td>
                <td className="px-3 py-2 text-ink-700 hidden lg:table-cell tabular">{d.updated}</td>
                <td className="px-3 py-2 hidden md:table-cell">
                  <span className="pill bg-cream-dark border border-border text-ink-700">{d.license}</span>
                </td>
                <td className="px-3 py-2 text-ink-700">{d.feeds}</td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-2">
                    {d.importable ? (
                      <Link
                        to={`/catalog-import?datasetId=${encodeURIComponent(d.id)}`}
                        className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md border border-foyr/40 text-foyr-deep hover:bg-foyr/10"
                        title={`Open importer pre-filled with ${d.id}`}
                      >
                        <CloudUpload className="w-3 h-3" /> import
                      </Link>
                    ) : null}
                    <a
                      href={d.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-ink-500 hover:text-ink-950 inline-flex items-center gap-1 text-[11px]"
                    >
                      open <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
